import { KeywordItemType } from '@/api/type/commonTypes';
import { Images } from '@/assets';
import { fonts } from '@/theme/fonts';
import { getTextWidth } from '@/utils/name/getTextWidth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface SelectListProps {
  options: KeywordItemType[];
  selectedOption: KeywordItemType;
  onSelect: (item: KeywordItemType) => void;
}
const SelectList = ({ options, selectedOption, onSelect }: SelectListProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<number>(0);
  const { t } = useTranslation();

  const getOptionTitle = useCallback(
    (item: KeywordItemType) => t(item.i18nKey),
    [t]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /** KeywordType 선택 */
  const handleSelect = useCallback(
    (keywordType: KeywordItemType) => {
      onSelect(keywordType);
      setOpen(false);
    },
    [onSelect]
  );

  useEffect(() => {
    const font = '14px Inter';
    const widths = options.map((o) => getTextWidth(getOptionTitle(o), font));

    const longest = Math.max(...widths);
    setMaxWidth(longest + 12 + 14 + 8 + 12);
  }, [options, getOptionTitle]);

  return (
    <SelectWrapper ref={ref} $width={maxWidth}>
      <Select onClick={() => setOpen((prev) => !prev)}>
        <div>{getOptionTitle(selectedOption)}</div>
        <DownArrowImg src={Images.DownArrowImg} alt="select-down-arrow" />
      </Select>

      {open && (
        <ListSection $width={maxWidth}>
          {options
            .filter((item) => item.keyword !== selectedOption.keyword)
            .map((item) => (
              <SelectItem key={item.keyword} onClick={() => handleSelect(item)}>
                {getOptionTitle(item)}
              </SelectItem>
            ))}
        </ListSection>
      )}
    </SelectWrapper>
  );
};

export default SelectList;

interface SelectWrapperProps {
  $width: number;
}
const SelectWrapper = styled.div<SelectWrapperProps>`
  position: relative;
  width: ${({ $width }) => ($width ? `${$width}px` : '120px')};
`;

const Select = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #ffffff;
  font-family: ${fonts.family.inter};
  font-size: 14px;
  color: #374151;
  cursor: pointer;
`;

const DownArrowImg = styled.img`
  width: 14px;
  height: 8px;
`;

interface ListSectionProps {
  $width: number;
}

const ListSection = styled.div<ListSectionProps>`
  position: absolute;
  top: calc(100% + 4px);
  padding: 6px 0;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  z-index: 10;
  width: ${({ $width }) => `${$width}px`};
`;

const SelectItem = styled.div`
  padding: 8px 12px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  &:hover {
    background: #eff6ff;
    color: #2563eb;
  }
`;
